import * as THREE from 'three';
import type { City, Place, PlaceKind } from '../world/city';
import type { PhysicsCity } from '../physics/world';
import type { Population, Person } from './population';
import { WalkingNetwork, type WalkNode } from './walking';
import { Reservations } from './reservations';
import { localFootRoute } from './local-foot-route';
import type { Vec } from '../destruction/structure';
export interface CivilTask {
  token: string;
  action: string;
  duration: number;
  state: 'active' | 'completed' | 'failed';
  reason: string;
  blockedAtOrigin?: boolean;
}
interface Routine {
  person: Person;
  role:
    | 'resident'
    | 'shopper'
    | 'shopkeeper'
    | 'builder'
    | 'maintenance'
    | 'traveller'
    | 'delivery'
    | 'visitor'
    | 'ferry-traveller';
  phase: number;
  destination?: Place;
  route: number[];
  ingress?: Vec[];
  cursor: number;
  waypoint?: { route: number[]; cursor: number; x: number; y: number; z: number; target: THREE.Vector3 };
  remaining: number;
  intent: string;
  reason: string;
  completed: number;
  replans: number;
  requests?: number;
  requestReason?: string;
  stuck: number;
  last: THREE.Vector3;
  carrying: string;
  selectedStock?: Place;
  serviceWait?: number;
  supportCache?: { owner: number; revision: number; x: number; y: number; z: number };
  occupiedNode?: { x: number; y: number; z: number; layer?: string; node: number };
  planPending: boolean;
  planVersion: number;
  commute?: Place;
  home?: Place;
  job?: Place;
  retry: number;
  traversal?: string;
  task?: CivilTask;
  occupancy?: {
    place: Place;
    phase: 'entering' | 'using' | 'leaving' | 'finished';
    from: THREE.Vector3;
    yaw: number;
    tilt: number;
    blend: number;
    elapsed: number;
    evacuating: boolean;
    hazard?: THREE.Vector3;
  };
  lift?: { from: THREE.Vector3; to: THREE.Vector3; elapsed: number; duration: number; resource: string };
  liftAccess?: { resource: string; landing: Vec; phase: 'approach' | 'exit' };
}
interface Plan {
  routine: Routine;
  epoch: number;
  destination: string;
  version: number;
  search: Generator<void, number[]>;
  ingress?: Vec[];
  queuedAt: number;
  queuedWall: number;
  slices: number;
  withoutOccupants?: { from: number; to: number };
  needsIngress?: boolean;
  ingressReachable?: boolean;
}
const CYCLES: Record<Routine['role'], PlaceKind[]> = {
  'ferry-traveller': ['home', 'dock', 'seat', 'display', 'counter'],
  visitor: ['home', 'watch', 'seat', 'exercise', 'display', 'counter'],
  delivery: ['loading'],
  shopkeeper: ['staff', 'display', 'seat'],
  resident: ['home', 'kitchen', 'desk', 'display', 'counter', 'seat'],
  shopper: ['display', 'counter', 'seat', 'home'],
  builder: ['materials', 'workbench', 'seat'],
  maintenance: ['maintenance', 'seat'],
  traveller: ['home', 'gate', 'desk', 'display', 'counter', 'seat'],
};
export class CivilLife {
  network!: WalkingNetwork;
  readonly reservations: Reservations;
  routines = new Map<string, Routine>();
  inventory = new Map<string, number>();
  interactions: { time: number; person: string; place: string; action: string; result: string }[] = [];
  private plans: Plan[] = [];
  private choices = new Map<string, { routine: Routine; version: number; queuedAt: number }>();
  private choiceMaxMs = 0;
  time = 0;
  purchases = 0;
  workDone = 0;
  maintenanceDone = 0;
  materialsUsed = 0;
  private epoch = 0;
  private occupiedNodes = new Map<number, string>();
  private placesByKind = new Map<PlaceKind, Place[]>();
  private placeBins = new Map<string, Place[]>();
  private interactionNodes = new Set<number>();
  private missionPlaces = new Map<number, Place>();
  private liftWaiters = new Map<string, string[]>();
  private occupancyAge = 1;
  private leaseAge = 0;
  private plansCompleted = 0;
  private plansFailed = 0;
  private waypointRecoveryChecks = 0;
  private waypointRecoveries = 0;
  private framePlanningMs?: number;
  /** The browser shares one route-planning allowance across all catch-up
   * steps. Activity clocks and physical movement still advance every step. */
  beginFrame(budgetMs = 1.8): void {
    this.framePlanningMs = Math.max(0, budgetMs);
  }
  constructor(
    readonly city: City,
    readonly physics: PhysicsCity,
    readonly population: Population,
  ) {
    this.reservations = new Reservations(() => physics.life.epoch);
    this.reset();
  }
  reset(restoredCity = false): void {
    for (const step of this.resetSteps(restoredCity)) void step;
  }
  *resetSteps(restoredCity = false): Generator<void> {
    this.routines.clear();
    this.reservations.clear();
    this.inventory.clear();
    this.plans = [];
    this.choices.clear();
    this.choiceMaxMs = 0;
    this.occupiedNodes.clear();
    this.liftWaiters.clear();
    this.occupancyAge = 1;
    this.leaseAge = this.plansCompleted = this.plansFailed = 0;
    this.waypointRecoveryChecks = this.waypointRecoveries = 0;
    this.interactions = [];
    this.time = this.purchases = this.workDone = this.maintenanceDone = this.materialsUsed = 0;
    this.epoch = this.physics.life.epoch;
    if (restoredCity && this.network) this.network.restore();
    else this.network = new WalkingNetwork(this.city, this.physics);
    this.placesByKind.clear();
    this.placeBins.clear();
    this.interactionNodes = new Set(this.network.places.values());
    for (const place of this.city.places ?? []) {
      if (place.access) continue;
      let group = this.placesByKind.get(place.kind);
      if (!group) this.placesByKind.set(place.kind, (group = []));
      group.push(place);
      const key = `${Math.floor(place.x / 8)},${Math.floor(place.z / 8)}`;
      const bin = this.placeBins.get(key) ?? [];
      bin.push(place);
      this.placeBins.set(key, bin);
    }
    const places = (this.city.places ?? []).filter((p) => !p.access && this.network.available(p));
    for (const p of places) if (p.kind === 'display' || p.kind === 'materials') this.inventory.set(p.id, 30);
    // Ordinary people retain connected pavement journeys. Only explicit
    // service, transport and military assignments enter the expensive planner.
    if (this.city.streetLife) return;
    const origins = places.filter((p) =>
      ['home', 'desk', 'display', 'seat', 'materials', 'maintenance', 'watch', 'exercise'].includes(p.kind),
    );
    let selected = origins.slice(0, this.city.regional ? this.population.density : 40);
    if (this.city.regional) {
      const central = origins
          .filter((p) => Math.hypot(p.x + 45, p.z + 30) < 180)
          .slice(0, Math.min(400, Math.ceil(this.population.density / 3))),
        used = new Set(central.map((p) => p.id)),
        outer = origins.filter((p) => !used.has(p.id)),
        count = Math.min(outer.length, this.population.density - central.length);
      selected = [
        ...central,
        ...Array.from({ length: count }, (_, i) => outer[Math.floor((i * outer.length) / count)]),
      ];
    }
    const homes = places.filter((p) => p.kind === 'home'),
      desks = places.filter((p) => p.kind === 'desk');
    const nearestPlace = (list: Place[], origin: Place, minimumSquared = 0): Place | undefined => {
      let best: Place | undefined,
        distance = Infinity;
      for (const candidate of list) {
        const d = (candidate.x - origin.x) ** 2 + (candidate.z - origin.z) ** 2;
        if (d >= minimumSquared && d < distance) {
          best = candidate;
          distance = d;
        }
      }
      return best;
    };
    for (const [index, place] of selected.entries()) {
      const person = this.population.people[index];
      if (!person) break;
      person.p.set(place.x, place.y, place.z);
      person.previous.copy(person.p);
      person.velocity.set(0, 0, 0);
      const role: Routine['role'] =
        this.city.coast &&
        ((place.z > 630 && place.x >= 180) || (place.kind === 'home' && place.z > 430 && place.x > 180))
          ? 'visitor'
          : place.kind === 'materials'
            ? 'builder'
            : place.kind === 'maintenance'
              ? 'maintenance'
              : place.kind === 'display'
                ? 'shopper'
                : 'resident';
      person.state = this.activity(place.kind);
      person.yaw = Math.PI;
      person.layer = this.network.nodes[this.network.places.get(place.id)!].layer;
      this.routines.set(person.id, {
        person,
        role,
        phase: Math.max(0, CYCLES[role].indexOf(place.kind)),
        destination: place,
        route: [],
        cursor: 0,
        remaining: 2 + (this.city.regional ? index % 80 : index) * 0.7,
        intent: `At ${place.name}`,
        reason: 'Staggered daily schedule',
        completed: 0,
        replans: 0,
        stuck: 0,
        last: person.p.clone(),
        carrying: '',
        planPending: false,
        planVersion: 0,
        home: place.kind === 'home' ? place : nearestPlace(homes, place),
        job:
          place.kind === 'desk'
            ? place
            : nearestPlace(desks, place, this.city.regional && index % 8 === 0 ? 450 ** 2 + 1e-8 : 0),
        retry: 0,
      });
      this.reservations.acquire(place.id, person.id, this.time, place.capacity, place.duration + 20);
      if (index % 16 === 15) yield;
    }
  }
  clear(): void {
    this.choices.clear();
    this.liftWaiters.clear();
    for (const place of this.missionPlaces.values()) this.network.places.delete(place.id);
    this.missionPlaces.clear();
    this.reservations.clear();
    this.routines.clear();
    this.plans = [];
  }
  remove(person: Person): void {
    const routine = this.routines.get(person.id);
    if (routine) this.cancelLift(routine);
    if (routine) routine.planVersion++;
    this.reservations.release(person.id);
    this.routines.delete(person.id);
    this.population.rail?.cancel(person.id, 'Resident population preference changed');
    this.population.buses?.cancel(person.id, 'Resident population preference changed');
  }
  enroll(person: Person): void {
    if (this.city.streetLife) return;
    if (this.routines.has(person.id)) return;
    const place = (this.city.places ?? [])
      .filter(
        (p) =>
          p.kind === 'home' &&
          this.network.available(p) &&
          !this.reservations.holders(p.id, this.time).length,
      )
      .sort(
        (a, b) =>
          Math.hypot(a.x - person.p.x, a.z - person.p.z) - Math.hypot(b.x - person.p.x, b.z - person.p.z),
      )[0];
    if (place) {
      person.p.set(place.x, place.y, place.z);
      person.previous.copy(person.p);
      person.layer = this.network.nodes[this.network.places.get(place.id)!].layer;
    }
    this.resume(person, person.layer ?? 'street');
    const r = this.routines.get(person.id)!;
    r.home = place;
    if (place) {
      this.reservations.acquire(place.id, person.id, this.time);
      this.request(r, place);
    }
  }
  finishTransit(person: Person, place: Place, reason: string, onwardDestination?: Place): void {
    const r = this.routines.get(person.id);
    if (!r) return;
    r.commute = onwardDestination;
    r.planPending = false;
    r.planVersion++;
    r.route = [];
    r.ingress = undefined;
    r.traversal = undefined;
    r.retry = 0;
    person.controlled = false;
    person.hidden = false;
    person.state = 'walk';
    person.detour = undefined;
    this.request(r, place);
    r.reason = reason;
  }
  /** A mission uses an existing supported graph node and the ordinary door,
   * lift, collision and reservation controller. It does not create a new path. */
  nodePlace(node: WalkNode): Place {
    let place = this.missionPlaces.get(node.id);
    if (!place) {
      place = {
        id: `mission-node:${node.id}`,
        kind: 'queue',
        name: node.layer.startsWith('interior:') ? 'Reserved indoor refuge' : 'Reserved street position',
        x: node.x,
        y: node.y,
        z: node.z,
        buildingId: node.buildingId,
        supportCell: node.supportCell,
        floor: Number(node.layer.split(':')[2] ?? 0),
        capacity: 1,
        duration: 1,
      };
      this.missionPlaces.set(node.id, place);
    }
    this.network.places.set(place.id, node.id);
    return place;
  }
  prepareForTask(person: Person): boolean {
    const r = this.routines.get(person.id);
    if (person.hidden || person.carrier || r?.lift) return false;
    const pose = r?.occupancy;
    if (pose && pose.phase !== 'finished') {
      if (pose.phase !== 'leaving') this.leavePose(r!, false);
      return false;
    }
    return (
      !person.fatal && !['incapacitated', 'falling', 'carried', 'recovering', 'flee'].includes(person.state)
    );
  }
  /** Assign an original crew member to an actual reserved interaction. Arrival,
   * clearance and activity time are handled by the same resident controller. */
  assignTask(person: Person, place: Place, token: string, action: string, duration: number): boolean {
    if (
      person.fatal ||
      ['incapacitated', 'falling', 'flee'].includes(person.state) ||
      !this.network.available(place) ||
      !this.reservations.acquire(place.id, person.id, this.time, place.capacity, 180)
    )
      return false;
    if (!this.routines.has(person.id)) this.resume(person, person.layer ?? 'street');
    const r = this.routines.get(person.id)!;
    this.clearPose(r);
    this.reservations.release(person.id);
    this.reservations.acquire(place.id, person.id, this.time, place.capacity, 180);
    r.task = { token, action, duration, state: 'active', reason: 'Walking to the task' };
    r.commute = undefined;
    r.retry = 0;
    r.traversal = undefined;
    r.remaining = duration;
    person.controlled = false;
    person.hidden = false;
    person.state = 'walk';
    this.request(r, place);
    r.intent = `${action} · ${place.name}`;
    return true;
  }
  task(person: Person): CivilTask | undefined {
    return this.routines.get(person.id)?.task;
  }
  taskPlanning(person: Person): boolean {
    return this.routines.get(person.id)?.planPending ?? false;
  }
  cancelTask(person: Person, reason: string, blockedAtOrigin = false): void {
    const r = this.routines.get(person.id);
    if (!r?.task) return;
    this.cancelLift(r);
    this.clearPose(r);
    r.task.state = 'failed';
    r.task.reason = reason;
    r.task.blockedAtOrigin = blockedAtOrigin;
    r.reason = reason;
    r.destination = undefined;
    r.route = [];
    r.ingress = undefined;
    r.planPending = false;
    r.planVersion++;
    r.traversal = undefined;
    this.reservations.release(person.id);
  }
  resume(person: Person, layer: string): void {
    this.reservations.release(person.id);
    if (person.fatal) {
      this.cancelTask(person, 'Persistent casualty cannot resume a civilian routine');
      person.hidden = false;
      person.controlled = false;
      person.carrier = undefined;
      person.state = 'incapacitated';
      return;
    }
    person.controlled = false;
    person.hidden = false;
    person.layer = layer;
    person.state = 'walk';
    person.detour = undefined;
    const r = this.routines.get(person.id);
    if (r) {
      this.cancelLift(r);
      this.clearPose(r);
      r.task = undefined;
      r.planVersion++;
      r.route = [];
      r.ingress = undefined;
      r.destination = undefined;
      r.planPending = false;
      r.phase = 0;
      r.retry = 0;
      r.traversal = undefined;
      return;
    }
    this.routines.set(person.id, {
      person,
      role: 'resident',
      phase: 0,
      route: [],
      cursor: 0,
      remaining: 0,
      intent: 'Returning home after care',
      reason: 'Planning a reachable home',
      completed: 0,
      replans: 0,
      stuck: 0,
      last: person.p.clone(),
      carrying: '',
      planPending: false,
      planVersion: 0,
      retry: 0,
    });
  }
  update(dt: number): void {
    this.time += dt;
    this.leaseAge += dt;
    if (this.leaseAge >= 1) {
      this.leaseAge = 0;
      for (const r of this.routines.values()) {
        if (['incapacitated', 'falling', 'flee'].includes(r.person.state)) continue;
        if (r.task && r.task.state !== 'active') continue;
        const place = r.commute ?? r.destination;
        if (place) this.reservations.acquire(place.id, r.person.id, this.time, place.capacity, 180);
      }
    }
    this.occupancyAge += dt;
    if (this.occupancyAge >= 0.25) {
      this.occupancyAge = 0;
      this.occupiedNodes.clear();
      for (const r of this.routines.values())
        if (!r.person.hidden && !r.route.length && !r.planPending) {
          const p = r.person.p,
            prior = r.occupiedNode,
            id =
              prior && prior.x === p.x && prior.y === p.y && prior.z === p.z && prior.layer === r.person.layer
                ? prior.node
                : this.network.nearest(p, r.person.layer),
            node = this.network.nodes[id];
          r.occupiedNode = { x: p.x, y: p.y, z: p.z, layer: r.person.layer, node: id };
          if (
            node &&
            Math.hypot(node.x - r.person.p.x, node.z - r.person.p.z) < 0.45 &&
            Math.abs(node.y - r.person.p.y) < 0.4
          )
            this.occupiedNodes.set(id, r.person.id);
        }
    }
    const start = performance.now();
    const planningBudget = this.framePlanningMs ?? 1.8;
    // Ending a shelter alert can release thousands of original routines at
    // once. Destination selection shares the existing planning allowance;
    // physical movement, support loss and activity clocks keep their own tick.
    let selected = 0;
    const choiceBudget = Math.min(0.7, planningBudget);
    while (this.choices.size && selected < 8 && performance.now() - start < choiceBudget) {
      const next = this.choices.entries().next().value!;
      this.choices.delete(next[0]);
      const { routine: r, version } = next[1],
        person = r.person;
      if (
        this.routines.get(person.id) !== r ||
        r.planVersion !== version ||
        r.task ||
        person.fatal ||
        person.controlled ||
        person.carrier ||
        ['falling', 'incapacitated', 'carried', 'recovering', 'flee'].includes(person.state)
      )
        continue;
      const began = performance.now();
      this.selectDestination(r);
      this.choiceMaxMs = Math.max(this.choiceMaxMs, performance.now() - began);
      selected++;
    }
    while (this.plans.length && performance.now() - start < planningBudget) {
      const plan = this.plans.shift()!,
        r = plan.routine;
      if (plan.version !== r.planVersion) {
        continue;
      }
      if (plan.epoch !== this.physics.life.epoch || r.destination?.id !== plan.destination) {
        r.planPending = false;
        continue;
      }
      plan.slices++;
      const result = plan.search.next();
      if (!result.done) {
        this.plans.push(plan);
        continue;
      }
      if (!result.value.length && plan.withoutOccupants) {
        const { from, to } = plan.withoutOccupants;
        plan.withoutOccupants = undefined;
        plan.search = this.network.search(
          from,
          to,
          this.time,
          undefined,
          false,
          r.person.id.startsWith('crew:'),
        );
        this.plans.push(plan);
        continue;
      }
      this.plansCompleted++;
      r.planPending = false;
      if (result.value.length) {
        r.route = result.value;
        r.ingress = plan.ingress;
        r.cursor = 0;
        r.person.state = 'walk';
        r.reason = 'Following an accessible route';
      } else {
        this.plansFailed++;
        r.retry = 6;
        const blockedAtOrigin = !!plan.needsIngress && !plan.ingressReachable;
        r.reason = blockedAtOrigin
          ? 'No supported exit from the current position'
          : 'Destination has no accessible route';
        this.reservations.release(r.person.id);
        r.destination = undefined;
        r.person.state = 'wait';
        if (r.task) this.cancelTask(r.person, r.reason, blockedAtOrigin);
      }
    }
    if (this.framePlanningMs !== undefined)
      this.framePlanningMs = Math.max(0, this.framePlanningMs - (performance.now() - start));
  }
  /** The existing per-person update observes physical injury transitions.
   * Avoid rescanning every healthy resident on every fixed step just to find
   * the few whose routine now needs releasing. */
  suspendInjured(person: Person): void {
    const r = this.routines.get(person.id);
    if (!r || !(r.destination || r.route.length || r.planPending || r.lift || r.liftAccess)) return;
    this.clearPose(r);
    this.reservations.release(person.id);
    r.route = [];
    r.ingress = undefined;
    r.planPending = false;
    r.destination = undefined;
    this.cancelLift(r);
    r.planVersion++;
    r.reason = 'Waiting for safe assistance';
    if (r.task) this.cancelTask(person, r.reason);
  }
  private request(r: Routine, place: Place, cause = 'New destination'): void {
    r.requests = (r.requests ?? 0) + 1;
    r.requestReason = cause;
    this.cancelLift(r);
    let from = this.network.nearest(r.person.p, r.person.layer);
    const to = this.network.places.get(place.id)!;
    r.destination = place;
    r.planPending = true;
    r.route = [];
    r.ingress = undefined;
    r.stuck = 0;
    r.serviceWait = 0;
    r.person.detour = undefined;
    r.last.copy(r.person.p);
    const blocked = new Set(
      [...this.occupiedNodes].filter(([, id]) => id !== r.person.id).map(([node]) => node),
    );
    let ingressCandidates: WalkNode[] | undefined;
    const hasExit = (node: WalkNode) =>
      node.id === to ||
      (this.network.adjacent.get(node.id) ?? []).some(
        (link) => (!link.oneWay || link.a === node.id) && this.network.open(link, this.time),
      );
    const connected = (node: WalkNode | undefined) =>
      !!node &&
      hasExit(node) &&
      Math.abs(node.y - r.person.p.y) < 0.6 &&
      this.network.lineOpen(r.person.p, node) &&
      this.physics.sweepFoot(r.person.p, {
        x: node.x - r.person.p.x,
        y: node.y - r.person.p.y,
        z: node.z - r.person.p.z,
      }) >= 0.999;
    if (!connected(this.network.nodes[from])) {
      // A short physical step onto an entrance tread can change the support
      // layer before a displaced/replanned actor has reached its old waypoint.
      // Reconcile from a nearby proven connection, never from a distant floor.
      const actual = this.network
        .near(r.person.p, 1.6)
        .filter((n) => Math.abs(n.y - r.person.p.y) < 0.3 && connected(n))
        .sort(
          (a, b) =>
            Math.hypot(a.x - r.person.p.x, a.z - r.person.p.z) -
            Math.hypot(b.x - r.person.p.x, b.z - r.person.p.z),
        )[0];
      if (actual) {
        from = actual.id;
        r.person.layer = actual.layer;
      }
    }
    if (blocked.has(from) || !connected(this.network.nodes[from])) {
      const alternate = this.network
        .near(r.person.p, 6, r.person.layer)
        .filter((n) => !blocked.has(n.id) && n.layer === r.person.layer && connected(n))
        .sort(
          (a, b) =>
            r.person.p.distanceToSquared(new THREE.Vector3(a.x, a.y, a.z)) -
            r.person.p.distanceToSquared(new THREE.Vector3(b.x, b.y, b.z)),
        )[0];
      if (alternate) from = alternate.id;
      else {
        ingressCandidates = this.network
          .near(r.person.p, 12, r.person.layer)
          .filter(
            (n) =>
              !blocked.has(n.id) &&
              hasExit(n) &&
              Math.abs(n.y - r.person.p.y) < 0.4 &&
              this.physics.ground(n, 0.35, 0.45),
          )
          .sort(
            (a, b) =>
              Math.hypot(a.x - r.person.p.x, a.z - r.person.p.z) -
              Math.hypot(b.x - r.person.p.x, b.z - r.person.p.z),
          )
          .slice(0, 4);
        if (!ingressCandidates.length) {
          r.planPending = false;
          r.retry = 2;
          r.reason = 'No supported connection from the current position to the walking network';
          if (r.task) this.cancelTask(r.person, r.reason, true);
          return;
        }
      }
    }
    const plan: Plan = {
      routine: r,
      epoch: this.physics.life.epoch,
      destination: place.id,
      version: ++r.planVersion,
      search: this.network.search(from, to, this.time, blocked, false, r.person.id.startsWith('crew:')),
      queuedAt: this.time,
      queuedWall: performance.now(),
      slices: 0,
      withoutOccupants: blocked.size && !ingressCandidates ? { from, to } : undefined,
      needsIngress: !!ingressCandidates,
    };
    if (ingressCandidates) plan.search = this.connectApproach(plan, ingressCandidates, to, blocked);
    this.plans.push(plan);
    r.intent = `${this.activity(place.kind)} · ${place.name}`;
    r.reason = 'Planning an accessible route';
  }
  private *connectApproach(
    plan: Plan,
    candidates: WalkNode[],
    to: number,
    blocked: Set<number>,
  ): Generator<void, number[]> {
    const start = plan.routine.person.p.clone();
    for (const candidate of candidates) {
      const approach = yield* localFootRoute(this.physics, start, candidate);
      if (!approach.length) continue;
      plan.ingressReachable = true;
      const route = yield* this.network.search(
        candidate.id,
        to,
        this.time,
        blocked,
        false,
        plan.routine.person.id.startsWith('crew:'),
      );
      if (route.length) {
        plan.ingress = approach;
        return route;
      }
    }
    return [];
  }
  private choose(r: Routine): void {
    if (this.choices.has(r.person.id)) return;
    this.clearPose(r);
    this.reservations.release(r.person.id);
    r.destination = undefined;
    r.route = [];
    r.reason = 'Choosing a reachable next activity';
    this.choices.set(r.person.id, { routine: r, version: r.planVersion, queuedAt: this.time });
  }
  private selectDestination(r: Routine): void {
    this.clearPose(r);
    this.reservations.release(r.person.id);
    r.destination = undefined;
    let kind = CYCLES[r.role][r.phase % CYCLES[r.role].length];
    const homeKitchen = (p: Place) => p.buildingId === r.home?.buildingId && p.floor === r.home?.floor;
    if (
      kind === 'kitchen' &&
      !this.placesByKind.get(kind)?.some((p) => homeKitchen(p) && this.network.available(p))
    ) {
      r.phase++;
      kind = CYCLES[r.role][r.phase % CYCLES[r.role].length];
    }
    const eligible = (p: Place) =>
      this.network.available(p) &&
      (kind !== 'kitchen' || homeKitchen(p)) &&
      (!this.occupiedNodes.has(this.network.places.get(p.id)!) ||
        this.occupiedNodes.get(this.network.places.get(p.id)!) === r.person.id) &&
      (kind !== 'counter' ||
        !r.selectedStock ||
        (p.buildingId === r.selectedStock.buildingId &&
          p.floor === r.selectedStock.floor &&
          p.tenant === r.selectedStock.tenant)) &&
      (p.kind !== 'display' || (this.inventory.get(p.id) ?? 0) > 0);
    const preferred =
      kind === 'home'
        ? r.home
        : kind === 'desk'
          ? r.job
          : kind === 'kitchen'
            ? this.placesByKind.get(kind)?.find(homeKitchen)
            : undefined;
    const reserve = (p: Place) => this.reservations.acquire(p.id, r.person.id, this.time, p.capacity, 180);
    let place = preferred && eligible(preferred) && reserve(preferred) ? preferred : undefined;
    // A resident's valid assigned home or job is already the preferred result.
    // Search and sort alternatives only when that actual reservation fails.
    if (!place) {
      const candidates = (this.placesByKind.get(kind) ?? []).filter(eligible);
      candidates.sort(
        (a, b) =>
          Math.hypot(a.x - r.person.p.x, a.z - r.person.p.z) +
          Math.abs(a.y - r.person.p.y) * 5 -
          (Math.hypot(b.x - r.person.p.x, b.z - r.person.p.z) + Math.abs(b.y - r.person.p.y) * 5),
      );
      place = candidates.find(reserve);
    }
    if (place) {
      const stop =
        this.population.rail?.book(r.person, place) ?? this.population.buses?.book(r.person, place);
      r.commute = stop ? place : undefined;
      this.request(r, stop ?? place);
    } else {
      r.retry = 3 + (r.person.variant % 3);
      r.person.state = 'wait';
      r.reason = `Waiting for an available ${kind}`;
      this.vacateInteraction(r);
    }
  }
  private vacateInteraction(r: Routine): void {
    const position = r.person.p;
    let atInteraction = false;
    for (let x = Math.floor((position.x - 0.7) / 8); x <= Math.floor((position.x + 0.7) / 8); x++)
      for (let z = Math.floor((position.z - 0.7) / 8); z <= Math.floor((position.z + 0.7) / 8); z++)
        if (
          this.placeBins
            .get(`${x},${z}`)
            ?.some((p) => Math.hypot(p.x - position.x, p.y - position.y, p.z - position.z) < 0.7)
        )
          atInteraction = true;
    if (!atInteraction) return;
    const candidates = this.network
      .near(r.person.p, 5, r.person.layer)
      .filter(
        (n) =>
          n.layer === r.person.layer &&
          !this.interactionNodes.has(n.id) &&
          !this.network.adjacent.get(n.id)?.some((l) => l.resource) &&
          Math.hypot(n.x - r.person.p.x, n.y - r.person.p.y, n.z - r.person.p.z) < 5 &&
          !this.occupiedNodes.has(n.id),
      )
      .sort(
        (a, b) =>
          r.person.p.distanceTo(new THREE.Vector3(a.x, a.y, a.z)) -
          r.person.p.distanceTo(new THREE.Vector3(b.x, b.y, b.z)),
      );
    for (const node of candidates) {
      const id = `queue:${node.id}`;
      if (!this.reservations.acquire(id, r.person.id, this.time, 1, 30)) continue;
      const place: Place = {
        ...node,
        id,
        kind: 'queue',
        name: 'Waiting space',
        floor: 0,
        capacity: 1,
        duration: 2,
      };
      this.network.places.set(id, node.id);
      this.interactionNodes.add(node.id);
      r.retry = 0;
      this.request(r, place);
      return;
    }
  }
  stepPerson(person: Person, dt: number): boolean {
    const r = this.routines.get(person.id);
    if (!r) return false;
    person.previous.copy(person.p);
    if (r.occupancy && !this.network.available(r.occupancy.place)) {
      this.clearPose(r);
      r.destination = undefined;
      r.route = [];
      r.ingress = undefined;
      r.planPending = false;
      r.planVersion++;
      this.reservations.release(person.id);
      person.state = 'falling';
      person.fallStart = person.p.y;
      person.velocity.y = 0;
      r.reason = 'Occupied furniture or its supporting floor was damaged';
      return true;
    }
    const danger = this.population.hazardAt(person.p);
    if (r.occupancy && r.occupancy.phase !== 'finished' && (person.state === 'flee' || danger)) {
      if (r.occupancy.phase !== 'leaving') this.leavePose(r, true);
      r.occupancy.evacuating = true;
      if (danger) r.occupancy.hazard = new THREE.Vector3(danger.x, danger.y, danger.z);
    }
    if (r.occupancy?.phase === 'entering' || r.occupancy?.phase === 'leaving') {
      this.stepPose(r, dt);
      return true;
    }
    if (person.state === 'flee') {
      this.cancelLift(r);
      this.clearPose(r);
      if (r.task) this.cancelTask(person, 'Evacuating a nearby hazard');
      this.reservations.release(person.id);
      r.route = [];
      r.ingress = undefined;
      r.destination = undefined;
      r.planPending = false;
      r.planVersion++;
      r.reason = 'Evacuating a nearby hazard';
      return false;
    }
    if (danger) {
      this.cancelLift(r);
      this.clearPose(r);
      if (r.task) this.cancelTask(person, 'Evacuating a nearby hazard');
      this.population.evacuate(person, danger);
      this.reservations.release(person.id);
      r.route = [];
      r.ingress = undefined;
      r.destination = undefined;
      r.planPending = false;
      r.planVersion++;
      return false;
    }
    r.retry = Math.max(0, r.retry - dt);
    if (r.liftAccess?.phase === 'exit') {
      const landing = r.liftAccess.landing;
      if (
        Math.hypot(person.p.x - landing.x, person.p.z - landing.z) >= 1.2 ||
        Math.abs(person.p.y - landing.y) > 0.6
      )
        this.releaseLiftAccess(r);
      else this.reservations.acquire(r.liftAccess.resource, person.id, this.time, 1, 4);
    }
    if (r.lift) {
      if (
        !r.destination ||
        !this.network.nodes[r.route[r.cursor]] ||
        !this.network.available(r.destination)
      ) {
        this.cancelLift(r);
        this.reservations.release(person.id);
        return true;
      }
      this.reservations.acquire(r.lift.resource, person.id, this.time, 1, r.lift.duration + 4);
      r.lift.elapsed += dt;
      person.hidden = true;
      person.p.copy(r.lift.from).lerp(r.lift.to, Math.min(1, r.lift.elapsed / r.lift.duration));
      if (r.lift.elapsed >= r.lift.duration) {
        if (!this.physics.ground(person.p, 0.2, 0.35)) {
          this.cancelLift(r);
          return true;
        }
        // A passenger stays inside the represented lift until the real lobby
        // has room. Releasing here used to stack multiple visible people at
        // one coordinate while a ring of new boarders blocked their exit.
        if (!this.population.footClear(person, person.p)) {
          r.reason = 'Lift arrived; waiting for a clear landing';
          return true;
        }
        person.hidden = false;
        r.liftAccess = { resource: r.lift.resource, landing: r.lift.to.clone(), phase: 'exit' };
        r.traversal = undefined;
        r.lift = undefined;
        r.cursor++;
        person.layer = this.network.nodes[r.route[r.cursor - 1]].layer;
      }
      return true;
    }
    person.think -= dt;
    if (person.think <= 0) {
      person.think = 0.1;
      const cached = r.supportCache;
      // A stationary interaction retains its support proof until that exact
      // support changes. Motion, fracture or a moving support always re-query.
      if (
        !cached ||
        cached.x !== person.p.x ||
        cached.y !== person.p.y ||
        cached.z !== person.p.z ||
        this.physics.staticSupportVersion(cached.owner) !== cached.revision
      ) {
        r.supportCache = undefined;
        const support =
          this.physics.footClearance.support(person.p, person.footCache) ??
          this.physics.ground(person.p, 0.35, 0.5);
        if (!support) {
          person.state = 'falling';
          person.fallStart = person.p.y;
          person.velocity.y = 0;
          return true;
        }
        person.support = support.owner;
        const revision = this.physics.staticSupportVersion(support.owner);
        if (revision !== undefined)
          r.supportCache = {
            owner: support.owner,
            revision,
            x: person.p.x,
            y: person.p.y,
            z: person.p.z,
          };
      }
    }
    if (r.planPending || r.retry > 0) return true;
    if (r.destination && !this.network.available(r.destination)) {
      this.reservations.release(person.id);
      r.destination = undefined;
      r.route = [];
      r.ingress = undefined;
      r.reason = 'Destination or its support was damaged';
      if (r.task) this.cancelTask(person, r.reason);
    }
    if (!r.destination) {
      if (r.task) return true;
      this.choose(r);
      return true;
    }
    if (r.ingress?.length) {
      const goal = r.ingress[0],
        distance = Math.hypot(goal.x - person.p.x, goal.y - person.p.y, goal.z - person.p.z);
      if (distance < 0.16) {
        r.ingress.shift();
        r.stuck = 0;
        return true;
      }
      person.state = 'walk';
      if (this.population.footClear(person, goal))
        this.population.walkTo(person, goal, dt, person.speed, true);
      else this.population.yieldFoot(person, goal, dt);
      const progress = r.last.distanceTo(person.p);
      r.stuck = progress > 0.01 ? 0 : r.stuck + dt;
      r.last.copy(person.p);
      r.reason = 'Following a supported local path around the parked obstruction';
      if (r.stuck > 5) {
        r.retry = 1;
        this.request(r, r.destination, 'Blocked local ingress');
      }
      return true;
    }
    if (r.route.length && r.cursor < r.route.length) {
      const node = this.network.nodes[r.route[r.cursor]],
        previous = r.cursor ? this.network.nodes[r.route[r.cursor - 1]] : undefined;
      const link = previous ? this.network.edge(previous.id, node.id) : undefined;
      if (link && !this.network.open(link, this.time)) {
        r.replans++;
        this.request(r, r.destination, 'Closed route link');
        return true;
      }
      const upcoming =
        r.cursor + 1 < r.route.length ? this.network.edge(node.id, r.route[r.cursor + 1]) : undefined;
      // A displaced pedestrian can already be beyond the graph's nearest
      // origin. Do not make the whole queue walk backwards onto that same
      // point. Skip only a short ordinary segment whose actual capsule and
      // every supporting floor sample are clear; doors/lifts/crossings retain
      // their admission and precise tread handling.
      if (!link?.resource && upcoming && !upcoming.resource && r.cursor + 1 < r.route.length) {
        const next = this.network.nodes[r.route[r.cursor + 1]],
          distance = Math.hypot(person.p.x - node.x, person.p.z - node.z),
          span = Math.hypot(person.p.x - next.x, person.p.z - next.z);
        if (
          // Ordinary approach already has precise arrival and swept walking.
          // Only a displaced walker beyond this point needs the longer probe.
          (person.p.x - node.x) * (next.x - node.x) + (person.p.z - node.z) * (next.z - node.z) >
            0.05 * Math.hypot(next.x - node.x, next.z - node.z) &&
          distance < 1.5 &&
          span < 4 &&
          next.layer === node.layer &&
          person.layer === node.layer &&
          !node.layer.startsWith('entrance:') &&
          Math.abs(next.y - person.p.y) < 0.15
        ) {
          this.waypointRecoveryChecks++;
          if (
            this.physics.sweepFoot(person.p, {
              x: next.x - person.p.x,
              y: next.y - person.p.y,
              z: next.z - person.p.z,
            }) >= 0.999
          ) {
            let supported = true;
            for (let i = 1, samples = Math.ceil(span / 0.5); i <= samples; i++) {
              const t = i / samples,
                floor = this.physics.ground(
                  {
                    x: person.p.x + (next.x - person.p.x) * t,
                    y: person.p.y,
                    z: person.p.z + (next.z - person.p.z) * t,
                  },
                  0.2,
                  0.25,
                );
              if (!floor || Math.abs(floor.y - person.p.y) > 0.15) {
                supported = false;
                break;
              }
            }
            if (supported) {
              this.waypointRecoveries++;
              r.cursor++;
              r.stuck = 0;
              r.last.copy(person.p);
              return true;
            }
          }
        }
      }
      let lift = link?.kind === 'lift' ? link : undefined,
        liftLanding = lift ? previous : undefined;
      // Reserve before entering the lobby, including its intermediate grid
      // waypoints. Waiting until the final point still lets a crowd surround
      // that point and obstruct the passenger trying to leave the lift.
      for (let i = r.cursor; !lift && i < Math.min(r.route.length - 1, r.cursor + 4); i++) {
        const candidate = this.network.edge(r.route[i], r.route[i + 1]);
        if (candidate?.kind === 'lift') {
          lift = candidate;
          liftLanding = this.network.nodes[r.route[i]];
        }
      }
      if (lift && liftLanding && !this.approachLift(r, liftLanding, lift.resource!, dt)) return true;
      if (link?.kind === 'crossing') {
        const green = this.population.crossings.green(link.junction ?? 0, this.time);
        this.population.crossings.request(
          link.resource!,
          person.id,
          previous!,
          node,
          this.time,
          green,
          r.traversal === link.resource,
        );
        if (r.traversal !== link.resource && (!green || !this.population.crossingClear(person.p, node))) {
          person.state = 'wait';
          r.reason = 'Waiting at the pedestrian crossing';
          return true;
        }
      }
      if (link?.kind === 'door' && link.resource && previous) {
        const dx = node.x - previous.x,
          dz = node.z - previous.z,
          length = Math.hypot(dx, dz);
        if (length > 0.01)
          for (const owner of this.reservations.holders(link.resource, this.time)) {
            const waiting = this.routines.get(owner)?.person;
            if (!waiting || owner === person.id) continue;
            const x = person.p.x - waiting.p.x,
              z = person.p.z - waiting.p.z;
            // A replan behind the queue must not reserve a stair lane away
            // from somebody already farther through it. Give its real front
            // occupant the lease; ordinary body clearance still applies.
            if (
              (x * dx + z * dz) / length > 0.35 &&
              Math.abs(x * dz - z * dx) / length < 0.7 &&
              Math.abs(person.p.y - waiting.p.y) < 1.5
            )
              this.reservations.release(owner, link.resource);
          }
      }
      // A pavement crossing has room for a bounded stream of walkers. Its
      // physical lane spacing and vehicle stop line remain authoritative;
      // serialising a whole road-width crossing to one person jams both ends.
      if (
        link?.resource &&
        !this.reservations.acquire(link.resource, person.id, this.time, link.kind === 'crossing' ? 16 : 1, 4)
      ) {
        person.state = 'wait';
        r.reason = `Waiting for ${link.kind} capacity`;
        return true;
      }
      if (link?.resource) r.traversal = link.resource;
      if (link?.kind === 'lift') {
        r.lift = {
          from: person.p.clone(),
          to: new THREE.Vector3(node.x, node.y, node.z),
          elapsed: 0,
          duration: 3 + Math.abs(node.y - person.p.y) / 1.2,
          resource: link.resource!,
        };
        person.state = 'lift';
        r.reason = 'Travelling inside the lift shaft';
        return true;
      }
      // At a tread edge the authoritative support may be one riser above the
      // waypoint. Horizontal arrival must not strand a resident on that riser.
      const arrival =
        r.cursor === r.route.length - 1 || node.layer.startsWith('entrance:')
          ? 0.13
          : node.layer === 'street'
            ? 0.65
            : 0.3;
      if (
        Math.hypot(person.p.x - node.x, person.p.z - node.z) < arrival &&
        Math.abs(person.p.y - node.y) < 0.36
      ) {
        // Keep ownership across all treads and through the doorway. A resident
        // who already entered a crossing finishes before its signal changes.
        if (r.traversal && !link?.resource && !upcoming?.resource) {
          this.reservations.release(person.id, r.traversal);
          r.traversal = undefined;
        }
        if (link?.kind === 'crossing') {
          this.reservations.release(person.id, link.resource);
          r.traversal = undefined;
        }
        person.layer = node.layer;
        r.cursor++;
        r.stuck = 0;
        r.last.copy(person.p);
        return true;
      }
      person.state = 'walk';
      r.reason = 'Walking to the reserved interaction';
      let target =
        r.waypoint?.route === r.route &&
        r.waypoint.cursor === r.cursor &&
        r.waypoint.x === node.x &&
        r.waypoint.y === node.y &&
        r.waypoint.z === node.z
          ? r.waypoint.target
          : undefined;
      if (!target) {
        target = new THREE.Vector3(node.x, node.y, node.z);
        if (previous && r.cursor < r.route.length - 1 && (!link?.resource || link.kind === 'crossing')) {
          // Ignore tiny corner connector segments when choosing the walking lane.
          // Their old 90-degree offsets sent a crossing's arriving stream into
          // the opposing waiting stream even though the pavement was wide enough.
          let before = previous,
            after = this.network.nodes[r.route[r.cursor + 1]];
          if (node.layer === 'street') {
            for (let i = r.cursor - 1; i >= Math.max(0, r.cursor - 4); i--) {
              const p = this.network.nodes[r.route[i]];
              if (p.layer !== node.layer) break;
              before = p;
              if (Math.hypot(p.x - node.x, p.z - node.z) > 0.7) break;
            }
            for (let i = r.cursor + 1; i <= Math.min(r.route.length - 1, r.cursor + 4); i++) {
              const p = this.network.nodes[r.route[i]];
              if (p.layer !== node.layer) break;
              after = p;
              if (Math.hypot(p.x - node.x, p.z - node.z) > 0.7) break;
            }
          }
          const direction = target.clone().sub(new THREE.Vector3(before.x, before.y, before.z));
          direction.y = 0;
          direction.normalize();
          if (node.layer === 'street' && after?.layer === 'street') {
            const leaving = new THREE.Vector3(after.x - node.x, 0, after.z - node.z).normalize(),
              divisor = 1 + direction.dot(leaving);
            if (divisor > 0.35)
              target.add(
                new THREE.Vector3(-direction.z - leaving.z, 0, direction.x + leaving.x)
                  .multiplyScalar(0.32 / divisor)
                  .clampLength(0, 0.5),
              );
            else target.add(new THREE.Vector3(-direction.z, 0, direction.x).multiplyScalar(0.32));
          } else target.add(new THREE.Vector3(-direction.z, 0, direction.x).multiplyScalar(0.28));
        }
        r.waypoint = { route: r.route, cursor: r.cursor, x: node.x, y: node.y, z: node.z, target };
      }
      // Walking lanes are an offset from the proven graph, not additional
      // clearance through furniture. After an actual stall, return this goal
      // to its authored centre; the ordinary capsule/ground/people checks still
      // decide every step. Keep that centre until the waypoint is reached.
      if (r.stuck > 0.35) {
        target.set(node.x, node.y, node.z);
        // A previous sidestep must not override this new heading after the
        // pedestrian-clearance check has accepted travel toward the centre.
        person.detour = undefined;
      }
      if (!this.population.footClear(person, target)) {
        r.reason = 'Yielding to another pedestrian';
        const x = person.p.x,
          z = person.p.z;
        this.population.yieldFoot(person, target, dt);
        // A body blocked on both sides is waiting. Keep its elapsed activity
        // time, while using the ordinary waiting cadence until space opens.
        if (person.p.x === x && person.p.z === z) person.state = 'wait';
        r.stuck += dt;
        if (r.stuck > 4) {
          r.replans++;
          this.request(r, r.destination, 'Pedestrian congestion');
          r.retry = 0.5;
        }
        return true;
      } else
        this.population.walkTo(
          person,
          target,
          dt,
          person.speed,
          link?.kind === 'door' || node.layer.startsWith('entrance:'),
        );
      // Sideways oscillation is not progress toward the current waypoint.
      if (r.last.distanceTo(target) - person.p.distanceTo(target) > 0.12) {
        r.stuck = 0;
        r.last.copy(person.p);
      } else r.stuck += dt;
      if (r.stuck > 5) {
        r.stuck = 0;
        r.replans++;
        this.network.invalidateAt(person.p);
        this.request(r, r.destination, 'No progress toward waypoint');
        r.retry = 0.6;
      }
      return true;
    }
    if (r.route.length) {
      r.route = [];
      r.ingress = undefined;
      r.remaining = r.task?.duration ?? r.destination.duration;
    }
    const place = r.destination;
    if (r.task) {
      if (r.task.state !== 'active') return true;
      if (!this.reservations.acquire(place.id, person.id, this.time, place.capacity, r.task.duration + 5)) {
        this.cancelTask(person, 'Task interaction is no longer available');
        person.state = 'wait';
        return true;
      }
      person.state = r.task.action;
      person.yaw = place.facing ?? person.yaw;
      if (place.pose && !r.occupancy) {
        this.enterPose(r, place, dt);
        return true;
      }
      r.intent = `${r.task.action} · ${place.name}`;
      r.task.reason = r.reason = 'Performing the reserved interaction';
      r.remaining -= dt;
      if (r.remaining <= 0) {
        if (r.occupancy?.phase === 'using') {
          this.leavePose(r, false);
          return true;
        }
        r.task.state = 'completed';
        r.task.reason = r.reason = 'Interaction completed at the destination';
        r.completed++;
        this.reservations.release(person.id);
        this.interactions.push({
          time: this.time,
          person: person.id,
          place: place.id,
          action: r.task.action,
          result: r.task.reason,
        });
        if (this.interactions.length > 512) this.interactions.shift();
      }
      return true;
    }
    if (place.kind === 'gate' && this.population.aviation?.waitAtGate(person, place)) {
      person.state = 'waiting for flight';
      r.intent = 'Reserved coastal excursion · awaiting boarding';
      r.reason = 'Waiting for aircraft turnaround and a free boarding stair';
      return true;
    }
    if (place.kind === 'dock' && this.population.marine?.waitAtDock(person, place)) {
      person.state = 'waiting for boat';
      r.intent = 'Coastal trip from a live reserved pier';
      r.reason = 'Waiting for an original craft, accessible berth and free passenger seat';
      return true;
    }
    if (place.kind === 'stop' && r.commute) {
      if (this.population.buses?.waiting(person)) {
        person.state = 'waiting for bus';
        r.intent = `Local bus to ${r.commute.name}`;
        r.reason = 'Waiting on pavement for a reserved passenger door';
        return true;
      }
      person.state = 'waiting for train';
      r.intent = `Railway to ${r.commute.name}`;
      r.reason = 'Waiting at the reserved platform';
      this.population.rail?.waitAtPlatform(person);
      return true;
    }
    if (!this.reservations.acquire(place.id, person.id, this.time, place.capacity, place.duration + 5)) {
      r.destination = undefined;
      r.reason = 'Interaction is occupied; selecting a free place';
      r.retry = 1;
      person.state = 'wait';
      this.vacateInteraction(r);
      return true;
    }
    if (place.pose && !r.occupancy) {
      this.enterPose(r, place, dt);
      return true;
    }
    if (
      place.kind === 'counter' &&
      this.population.shopStaff &&
      this.population.shopStaff.status(place) !== 'open'
    ) {
      r.serviceWait = (r.serviceWait ?? 0) + dt;
      if (this.population.shopStaff.status(place) === 'closed' || r.serviceWait > 45) {
        r.carrying = '';
        r.selectedStock = undefined;
        person.carry = '';
        r.phase++;
        this.choose(r);
        r.reason = 'Checkout unavailable; leaving unpurchased goods in the shop';
      } else {
        person.state = 'waiting for clerk';
        r.reason = 'Waiting for the original clerk to return to checkout';
      }
      return true;
    }
    person.state = this.activity(place.kind);
    person.yaw = r.occupancy?.phase === 'using' ? place.pose!.yaw : (place.facing ?? Math.PI);
    r.intent = `${person.state} · ${place.name}`;
    r.reason = 'Performing the reserved interaction';
    r.remaining -= dt;
    if (r.remaining <= 0) {
      if (r.occupancy?.phase === 'using') {
        this.leavePose(r, false);
        return true;
      }
      if (place.kind === 'queue') {
        this.choose(r);
        return true;
      }
      let result = 'Completed';
      if (place.kind === 'desk') {
        this.workDone++;
        result = 'Work session completed';
      }
      if (place.kind === 'display') {
        r.selectedStock = place;
        r.carrying = 'selected goods';
      }
      if (place.kind === 'counter' && r.carrying === 'selected goods') {
        const stock = r.selectedStock,
          count = stock ? (this.inventory.get(stock.id) ?? 0) : 0;
        if (stock && stock.buildingId === place.buildingId && count > 0 && this.network.available(stock)) {
          this.inventory.set(stock.id, count - 1);
          this.purchases++;
          r.carrying = 'shopping bag';
          result = 'One stocked item purchased';
        } else {
          r.carrying = '';
          result = 'Shop is out of stock';
        }
        r.selectedStock = undefined;
      }
      if (place.kind === 'materials' && (this.inventory.get(place.id) ?? 0) > 0) {
        this.inventory.set(place.id, this.inventory.get(place.id)! - 1);
        r.carrying = 'materials';
      }
      if (place.kind === 'workbench' && r.carrying === 'materials') {
        this.materialsUsed++;
        r.carrying = '';
        result = 'Material used by the work task';
      }
      if (place.kind === 'maintenance') {
        this.maintenanceDone++;
        result = 'Fixture inspected and service logged';
      }
      if (place.kind === 'home') {
        r.carrying = '';
        r.selectedStock = undefined;
      }
      r.completed++;
      r.phase++;
      person.carry = r.carrying;
      this.interactions.push({
        time: this.time,
        person: person.id,
        place: place.id,
        action: person.state,
        result,
      });
      if (this.interactions.length > 512) this.interactions.shift();
      this.choose(r);
    }
    return true;
  }
  private enterPose(r: Routine, place: Place, dt: number): void {
    const person = r.person;
    r.occupancy = {
      place,
      phase: 'entering',
      from: person.p.clone(),
      yaw: person.yaw,
      tilt: 0,
      blend: 0,
      elapsed: 0,
      evacuating: false,
    };
    person.state = this.activity(place.kind);
    person.seatBlend = 0;
    this.stepPose(r, dt);
  }
  private releaseLiftAccess(r: Routine): void {
    const access = r.liftAccess;
    if (!access) return;
    this.reservations.release(r.person.id, access.resource);
    const queue = this.liftWaiters.get(access.resource)?.filter((id) => id !== r.person.id);
    if (queue?.length) this.liftWaiters.set(access.resource, queue);
    else this.liftWaiters.delete(access.resource);
    if (r.traversal === access.resource) r.traversal = undefined;
    r.liftAccess = undefined;
  }
  private approachLift(r: Routine, landing: WalkNode, resource: string, dt: number): boolean {
    const p = r.person,
      distance = Math.hypot(p.p.x - landing.x, p.p.z - landing.z);
    if (distance > 3) return true;
    if (r.liftAccess?.resource !== resource) {
      this.releaseLiftAccess(r);
      r.liftAccess = { resource, landing, phase: 'approach' };
      const queue = this.liftWaiters.get(resource) ?? [];
      queue.push(p.id);
      this.liftWaiters.set(resource, queue);
    }
    const first = this.liftWaiters.get(resource)?.[0];
    if (first === p.id && this.reservations.acquire(resource, p.id, this.time, 1, 4)) return true;
    r.stuck = 0;
    r.reason = 'Waiting clear of the lift landing';
    p.state = 'wait';
    if (distance < 1.65) {
      const away = new THREE.Vector3(p.p.x - landing.x, 0, p.p.z - landing.z);
      if (away.lengthSq() < 0.01) away.set(Math.sin(p.variant * 2.4), 0, Math.cos(p.variant * 2.4));
      const target = new THREE.Vector3(landing.x, p.p.y, landing.z).addScaledVector(away.normalize(), 1.8),
        before = p.p.clone();
      if (this.population.footClear(p, target)) this.population.walkTo(p, target, dt, p.speed * 0.7);
      else this.population.yieldFoot(p, target, dt);
      if (p.p.distanceToSquared(before) > 1e-8) p.state = 'yielding';
    }
    return false;
  }
  private cancelLift(r: Routine): void {
    this.releaseLiftAccess(r);
    if (!r.lift) return;
    this.reservations.release(r.person.id, r.lift.resource);
    r.lift = undefined;
    const person = r.person;
    person.hidden = false;
    // Keep the original passenger where the interruption happened. A missing
    // floor is a real fall, not a teleport to the old lift destination.
    if (!this.physics.ground(person.p, 0.35, 0.5) && person.state !== 'incapacitated') {
      person.fallStart = person.p.y;
      person.state = 'falling';
      person.velocity.y = 0;
    }
  }
  private clearPose(r: Routine): void {
    r.occupancy = undefined;
    r.person.poseTilt = undefined;
    r.person.seatHeight = undefined;
    r.person.seatBlend = undefined;
  }
  private leavePose(r: Routine, evacuating: boolean): void {
    const o = r.occupancy!;
    o.phase = 'leaving';
    o.from.copy(r.person.p);
    o.yaw = r.person.yaw;
    o.tilt = r.person.poseTilt ?? 0;
    o.blend = r.person.seatBlend ?? 1;
    o.elapsed = 0;
    o.evacuating = evacuating;
  }
  private stepPose(r: Routine, dt: number): void {
    const o = r.occupancy!,
      pose = o.place.pose!,
      p = r.person,
      entering = o.phase === 'entering',
      duration = pose.duration * (o.evacuating ? 0.65 : 1),
      elapsed = Math.min(duration, o.elapsed + dt),
      t = elapsed / duration,
      end = entering
        ? new THREE.Vector3(pose.x, pose.y + (pose.posture === 'lying' ? pose.seatHeight : 0), pose.z)
        : new THREE.Vector3(o.place.x, o.place.y, o.place.z),
      next = o.from.clone().lerp(end, t),
      yaw = entering ? pose.yaw : Math.atan2(o.from.x - end.x, o.from.z - end.z),
      nextYaw =
        o.yaw +
        Math.atan2(Math.sin(yaw - o.yaw), Math.cos(yaw - o.yaw)) * (entering ? Math.min(1, t / 0.35) : t),
      nextTilt = entering ? (pose.posture === 'lying' ? (-Math.PI / 2) * t : 0) : o.tilt * (1 - t);
    // Only the reserved, still-intact furniture is exempted during sitting or
    // climbing into bed. All walls, other furniture and people remain solid.
    if (
      !this.population.footClear(p, next) ||
      !this.physics.interactionClear(
        p.p,
        next,
        p.yaw,
        nextYaw,
        p.poseTilt ?? 0,
        nextTilt,
        p.scale,
        o.place.furnitureId,
      )
    ) {
      r.reason = 'Waiting for a clear furniture approach or exit';
      return;
    }
    o.elapsed = elapsed;
    p.p.copy(next);
    p.yaw = nextYaw;
    p.poseTilt = nextTilt;
    p.seatBlend = entering ? t : o.blend * (1 - t);
    p.seatHeight = pose.seatHeight;
    p.state = this.activity(o.place.kind);
    r.reason = entering
      ? 'Entering the reserved furniture'
      : o.evacuating
        ? 'Leaving furniture to evacuate'
        : 'Leaving the completed interaction';
    if (t >= 1) {
      if (entering) {
        o.phase = 'using';
        r.reason = pose.posture === 'lying' ? 'Resting on the reserved bed' : 'Using the reserved seat';
      } else {
        o.phase = 'finished';
        p.poseTilt = p.seatHeight = p.seatBlend = undefined;
        if (o.evacuating) {
          const hazard = o.hazard ?? this.population.hazardAt(p.p);
          if (hazard) this.population.evacuate(p, hazard);
          else {
            // An external incident may already have assigned the escape goal.
            p.state = 'flee';
            p.timer = Math.max(p.timer, 6);
          }
        }
      }
    }
  }
  private activity(kind: PlaceKind): string {
    return {
      home: 'resting',
      watch: 'watching water',
      exercise: 'stretching',
      desk: 'working',
      display: 'browsing',
      counter: 'purchasing',
      kitchen: 'preparing a meal',
      staff: 'serving customers',
      seat: 'sitting',
      materials: 'collecting',
      workbench: 'building',
      maintenance: 'inspecting',
      loading: 'unloading',
      stop: 'waiting for transit',
      gate: 'waiting for flight',
      dock: 'waiting for boat',
      queue: 'waiting',
    }[kind];
  }
  inspect(id?: string): Record<string, unknown>[] {
    return [...this.routines.values()]
      .filter((r) => !id || r.person.id === id)
      .map((r) => ({
        id: r.person.id,
        role: r.role,
        activity: r.person.state,
        intent: r.intent,
        reason: r.reason,
        destination: r.destination?.id,
        position: r.person.p.toArray(),
        layer: r.person.layer,
        support: r.person.support,
        route: r.route.slice(r.cursor).map((id) => this.network.nodes[id]),
        reserved: r.destination ? this.reservations.holders(r.destination.id, this.time) : [],
        completed: r.completed,
        replans: r.replans,
        requests: r.requests ?? 0,
        requestReason: r.requestReason,
        waypoint: r.waypoint?.target.toArray(),
        ingress: r.ingress,
        stuck: r.stuck,
        carrying: r.person.carry || r.carrying,
        hidden: !!r.person.hidden,
        planning: r.planPending,
        task: r.task ? { ...r.task } : undefined,
        furniture: r.occupancy
          ? {
              id: r.occupancy.place.furnitureId,
              phase: r.occupancy.phase,
              posture: r.occupancy.place.pose?.posture,
              elapsed: r.occupancy.elapsed,
            }
          : undefined,
        planningSeconds:
          this.plans.find((p) => p.routine === r)?.queuedAt === undefined
            ? 0
            : this.time - this.plans.find((p) => p.routine === r)!.queuedAt,
      }));
  }
  snapshot(): Record<string, number> {
    return {
      purposefulResidents: this.routines.size,
      activityInteractions: this.interactions.length,
      purchases: this.purchases,
      workSessions: this.workDone,
      maintenanceTasks: this.maintenanceDone,
      constructionMaterialsUsed: this.materialsUsed,
      activityReservations: this.reservations.size,
      activityChoicesPending: this.choices.size,
      activityChoiceOldestSeconds: this.choices.size
        ? this.time - this.choices.values().next().value!.queuedAt
        : 0,
      activityChoiceMaxMs: this.choiceMaxMs,
      routePlansPending: this.plans.length,
      routePlansCompleted: this.plansCompleted,
      routePlansFailed: this.plansFailed,
      routeOldestSeconds: this.plans.length ? Math.max(...this.plans.map((p) => this.time - p.queuedAt)) : 0,
      routeOldestWallMs: this.plans.length
        ? Math.max(...this.plans.map((p) => performance.now() - p.queuedWall))
        : 0,
      walkNodes: this.network.nodes.length,
      walkSearches: this.network.searchCount,
      walkSearchVisits: this.network.searchVisits,
      walkSearchLinkChecks: this.network.searchLinkChecks,
      walkCompressedSteps: this.network.compressedSearchSteps,
      walkPrunedInteriorBranches: this.network.prunedInteriorBranches,
      waypointRecoveryChecks: this.waypointRecoveryChecks,
      waypointRecoveries: this.waypointRecoveries,
      walkLinks: this.network.links.length,
      representedInteriorFloors: (this.city.interiors ?? []).reduce((n, r) => n + r.floors.length, 0),
      occupiedInteriorFloors: new Set(
        [...this.routines.values()]
          .filter((r) => r.person.layer?.startsWith('interior:'))
          .map((r) => r.person.layer),
      ).size,
    };
  }
}
